import { HiddenUsernameField } from '@avanta/shared/components/HiddenUsernameField';
import { PasswordRequirement } from '@avanta/shared/components/PasswordRequirement';
import { Button } from '@avanta/shared/components/ui/Button';
import { Card } from '@avanta/shared/components/ui/Card';
import { LOCIZE_NS } from '@avanta/shared/constants/common';
import { cn } from '@avanta/shared/utils/cn';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AuthService } from 'services/auth';
import { useUserStore } from 'stores/user';
import { type FormData } from 'types/form';
import { changePasswordForm } from './model';

export const ChangePassword = (): React.ReactElement | null => {
  const { t } = useTranslation(LOCIZE_NS.PROFILE);
  const user = useUserStore(state => state.user);
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty, isValid },
    reset,
    watch,
  } = useForm<FormData>({
    defaultValues: {
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
    },
    mode: 'onChange',
  });
  const newPasswordValue = watch('newPassword');
  const isSubmitDisabled = !isDirty || !isValid || isSubmitting;

  const handleOnSubmit = async (data: FormData): Promise<void> => {
    try {
      const currentPassword = String(data.currentPassword);
      const newPassword = String(data.newPassword);

      await AuthService.changeUserPassword({
        currentPassword,
        newPassword,
      });
      reset();
      toast.success(t('ChangePassword.success'));
    } catch {
      toast.error(t('ChangePassword.error.generic'));
    }
  };

  return (
    <Card className="bg-white" title={t('ChangePassword.heading')}>
      <form className="flex flex-col gap-6" onSubmit={handleSubmit(handleOnSubmit)}>
        <HiddenUsernameField value={user?.email ?? ''} />
        <div className="grid grid-cols-1 am-lg:grid-cols-2 gap-6">
          {Object.entries(changePasswordForm).map(([formId, input]) => {
            const { component: Component } = input;
            const formErrors = errors[formId];
            const registerOptions = {
              required: input.rules?.required ?? input.required,
              pattern: input.pattern,
              minLength: input.rules?.minLength,
              validate: input.rules?.validate ?? input.validate,
            };
            return (
              <div
                key={formId}
                className={cn(
                  'w-full',
                  formId === 'currentPassword' && 'am-lg:col-span-2 am-lg:w-[calc(50%-14px)]'
                )}
              >
                <Component
                  type={input.type}
                  control={control}
                  className="w-full"
                  label={t(input.label)}
                  placeholder={t(input.placeholder ?? '')}
                  options={input.options ?? []}
                  rules={input.rules ?? {}}
                  showPassword={input.type === 'password'}
                  {...register(formId, registerOptions)}
                  autoComplete={input.autocomplete}
                  variant={!!formErrors && formId !== 'newPassword' ? 'invalid' : 'filled'}
                  helperText={
                    formErrors?.message && formId !== 'newPassword'
                      ? t(formErrors.message)
                      : undefined
                  }
                />
                {formId === 'newPassword' && (
                  <PasswordRequirement password={String(newPasswordValue)} />
                )}
              </div>
            );
          })}
        </div>
        <Button
          type="submit"
          className="w-full am-lg:w-fit min-w-45"
          disabled={isSubmitDisabled}
          isLoading={isSubmitting}
        >
          {t('ChangePassword.cta')}
        </Button>
      </form>
    </Card>
  );
};
